import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { NotificationsActions } from './notifications.actions';
import { NotificationDTO } from '../data/NotificationDTO';
import { NotificationsApiService } from '../services/notifications-api.service';
import { firstValueFrom } from 'rxjs';
import { NotificationUtil } from '@shared/util/notification.util';
import { NotificationsFacadeService } from 'app/view/notifications/services/notifications-facade.service';
import { PageCriteriaDTO } from '@data/common/pagination/PageCriteriaDTO';

export interface NotificationsStateModel {
  notifications: NotificationDTO[];

  notificationsCriteria?: PageCriteriaDTO;

  totalElements: number;

  hasUnreadNotifications: boolean;
}

@State<NotificationsStateModel>({
  name: 'notifications',
  defaults: {
    notifications: [],
    notificationsCriteria: new PageCriteriaDTO(),
    totalElements: 0,
    hasUnreadNotifications: false
  }
})
@Injectable()
export class NotificationsState {
  constructor(private notificationsApiService: NotificationsApiService, private notificationsFacadeService: NotificationsFacadeService,
              private notificationUtil: NotificationUtil) {
  }

  @Action(NotificationsActions.getInitial)
  async getInitial(ctx: StateContext<NotificationsStateModel>) {
    const notificationsCriteria = new PageCriteriaDTO();
    notificationsCriteria.pageSize = 50;
    const page = await firstValueFrom(this.notificationsApiService.getPage(notificationsCriteria));

    await this.notificationsFacadeService.markAsRead(page.content);

    ctx.patchState({
      totalElements: page.totalElements,
      notificationsCriteria,
      notifications: page.content ?? []
    });
  }

  @Action(NotificationsActions.loadMore)
  async loadMore(ctx: StateContext<NotificationsStateModel>) {
    const state = ctx.getState();
    const notificationsCriteria = new PageCriteriaDTO();

    notificationsCriteria.pageNumber = state.notificationsCriteria.pageNumber + 1;
    notificationsCriteria.pageSize = state.notificationsCriteria.pageSize;

    const { pageNumber, pageSize } = notificationsCriteria;
    const totalPages = Math.ceil(state.totalElements / pageSize);
    if (pageNumber > totalPages) {
      return;
    }

    const page = await firstValueFrom(this.notificationsApiService.getPage(notificationsCriteria));

    await this.notificationsFacadeService.markAsRead(page.content);

    ctx.patchState({
      totalElements: page.totalElements,
      notificationsCriteria,
      notifications: [ ...state.notifications, ...page.content ]
    });
  }

  @Action(NotificationsActions.remove)
  async remove(ctx: StateContext<NotificationsStateModel>, action: NotificationsActions.remove) {
    await firstValueFrom(this.notificationsApiService.remove(action.notificationId));
    ctx.patchState({
      notifications: ctx.getState().notifications.filter(n => n.id !== action.notificationId)
    });
    this.notificationUtil.success('COMPONENT.NOTIFICATIONS.DELETE_SUCCESS');
  }

  @Action(NotificationsActions.setHasUnreadNotifications)
  async setHasUnreadNotifications(ctx: StateContext<NotificationsStateModel>, action: NotificationsActions.setHasUnreadNotifications) {
    ctx.patchState({
      hasUnreadNotifications: action.hasUnreadNotifications
    });
  }

}
